// src/app/hooks/useReportGenerator.ts
import { useCallback, useEffect, useState } from 'react';
import { generateReport, fetchReports } from '@/app/services/reporting/insights-api-service';
import type { GeneratedReport, ReportGenerateRequest } from '../../components/reporting/types';

// ── Generate ──────────────────────────────────────────────────────────────────

interface UseGenerateReportResult {
  generate:     (req: ReportGenerateRequest) => Promise<void>;
  isGenerating: boolean;
  report:       GeneratedReport | null;
  error:        Error | null;
  reset:        () => void;
}

export function useGenerateReport(): UseGenerateReportResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [report,       setReport]       = useState<GeneratedReport | null>(null);
  const [error,        setError]        = useState<Error | null>(null);

  const generate = useCallback(async (req: ReportGenerateRequest) => {
    setIsGenerating(true);
    setError(null);
    try {
      const result = await generateReport(req);
      setReport(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Generation failed'));
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const reset = useCallback(() => { setReport(null); setError(null); }, []);

  return { generate, isGenerating, report, error, reset };
}

// ── History ───────────────────────────────────────────────────────────────────

interface UseReportHistoryResult {
  reports:   GeneratedReport[];
  isLoading: boolean;
  error:     Error | null;
  refetch:   () => void;
}

export function useReportHistory(brandName: string): UseReportHistoryResult {
  const [reports,   setReports]   = useState<GeneratedReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState<Error | null>(null);
  const [tick,      setTick]      = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!brandName) return;
    let cancelled = false;
    setIsLoading(true);
    fetchReports(brandName)
      .then((data) => { if (!cancelled) setReports(data); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err : new Error('Failed to load reports')); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [brandName, tick]);

  return { reports, isLoading, error, refetch };
}